import { marked } from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({ gfm: true, breaks: true })

/**
 * Renders a description or comment to safe HTML.
 *
 * Mentions are applied by walking the sanitised DOM and building real nodes,
 * never by assembling markup from text. That matters more than it looks:
 * `textContent` hands back decoded characters, so round-tripping it through
 * `innerHTML` would turn a comment containing `@marat <img src=x onerror=…>`
 * into a live element after DOMPurify had already neutralised it.
 *
 * Walking the DOM also means `anna@artsol.dev` in a sentence, an `@media` rule
 * inside a code block, and link text are all left alone — the cases a regular
 * expression over markup gets wrong every time.
 */
export function renderMarkdown(source: string): string {
  const raw = marked.parse(source ?? '', { async: false })
  const clean = DOMPurify.sanitize(raw, {
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#|\/)/i,
  })

  const doc = new DOMParser().parseFromString(`<div>${clean}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return clean

  highlightMentions(root)
  normaliseCheckboxes(root)
  return root.innerHTML
}

/**
 * Renders `- [ ]` written inside prose as a read-only checkbox.
 *
 * The task's real checklist is the subtask list, which has its own rows and
 * ids so a single item can be ticked on its own. This exists only so that
 * Markdown checkbox syntax appearing in a description does not look broken:
 * marked emits an `<input>` for it, and DOMPurify checks `type` on an input
 * against ALLOWED_URI_REGEXP — because of `type="image"` — so the strict URL
 * policy above silently drops `type="checkbox"` and it would otherwise render
 * as a text field.
 */
function normaliseCheckboxes(root: Element) {
  root.querySelectorAll('li > input').forEach((box) => {
    box.setAttribute('type', 'checkbox')
    box.setAttribute('disabled', '')
    box.parentElement?.classList.add('task-item')
  })
}

// An @ that follows the start of a line or a space. The leading boundary is
// what stops an email address from reading as a mention.
const MENTION_GLOBAL = /(^|\s)@([A-Za-z0-9][A-Za-z0-9._-]*)/g
const MENTION_TEST = /(^|\s)@[A-Za-z0-9]/

function highlightMentions(root: Element) {
  const doc = root.ownerDocument
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let el = node.parentElement
      while (el && el !== root) {
        const tag = el.tagName.toLowerCase()
        if (tag === 'code' || tag === 'pre' || tag === 'a') return NodeFilter.FILTER_REJECT
        el = el.parentElement
      }
      // A fresh non-global regex: /g carries lastIndex between calls and would
      // start skipping matches.
      return MENTION_TEST.test(node.textContent ?? '')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
    },
  })

  const targets: Text[] = []
  while (walker.nextNode()) targets.push(walker.currentNode as Text)

  for (const node of targets) {
    const text = node.textContent ?? ''
    const pieces: Node[] = []
    let cursor = 0

    MENTION_GLOBAL.lastIndex = 0
    for (let m = MENTION_GLOBAL.exec(text); m; m = MENTION_GLOBAL.exec(text)) {
      const [full, lead = '', handle = ''] = m
      const start = m.index + lead.length

      if (start > cursor) pieces.push(doc.createTextNode(text.slice(cursor, start)))

      const span = doc.createElement('span')
      span.className = 'mention'
      span.textContent = `@${handle}` // textContent, so nothing can be markup
      pieces.push(span)

      cursor = m.index + full.length
    }

    if (cursor < text.length) pieces.push(doc.createTextNode(text.slice(cursor)))
    if (pieces.length > 0) node.replaceWith(...pieces)
  }
}

/** Strips markup for card previews and search results. */
export function plainText(source: string, limit = 160): string {
  const flat = (source ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return flat.length > limit ? `${flat.slice(0, limit)}…` : flat
}
